import React, {useMemo} from "react";
import PublicLayout from "modules/ui/components/PublicLayout";
import useTranslate from "../../modules/core/languages/hooks/useTranslate";
import {useParams} from "react-router-dom";
import ButtonPrimary from "../../modules/ui/shared/Button/ButtonPrimary";
import useTransferDetailsGet from "../../modules/transfers/hooks/useTransferDetailsGet";
import ConfirmationNumberCard from "../../modules/transferOk/components/ConfirmationNumberCard";
import PersonalDetailsSection from "../../modules/transferOk/components/PersonalDetailsSection";
import EventDetailsSection from "../../modules/transferOk/components/EventDetailsSection";
import TransportDetailsSection from "../../modules/transferOk/components/TransportDetailsSection";
import TransferDetailsSection from "../../modules/transferOk/components/TransferDetailsSection";
import AlertStatusSection from "../../modules/transferOk/components/AlertStatusSection";
import useCustomerContext from "../../modules/customers/hooks/useCustomerContext";
import Transfer from "../../modules/transfers/types/Transfer";
import {TransferStatusEnum} from "../../modules/transfers/enums/TransferStatusEnum";
import PaymentPanel, {PaymentPanelPaymentInput} from "../../modules/payment/components/PaymentPanel";
import {useOidcClientContext} from "../../modules/ui/context/OidcClientContext";
import TransferPriceSummary from "../../modules/transferOk/components/TransferPriceSummary";
import useTransferPayWithSavedCard from "../../modules/transfers/hooks/useTransferPayWithSavedCard";
import {Tpv} from "../../modules/payment/enum/Tpv";

function PageTransferOK() {
  const {uuid} = useParams();
  const t = useTranslate();
  const {customer} = useCustomerContext();
  const {authenticated} = useOidcClientContext();

  const {
    transferInfo,
    cityEventInfo,
    eventInfo,
    cityInfo,
    departmentData,
    locationFrom,
    locationTo,
    parentTransfer,
    route,
    isLoading
  } = useTransferDetailsGet(uuid);

  const payWithCard = useTransferPayWithSavedCard();

  const transferCardUuid = useMemo(() => {
    if (!transferInfo?.paymentCard) return null;
    const parts = transferInfo.paymentCard.split('/');
    return parts[parts.length - 1] || null;
  }, [transferInfo?.paymentCard]);

  const showPaymentPanel = (t?: Transfer): boolean => {
    if (!t) return false;
    if (customer.type !== 2) return false;
    if (departmentData?.name?.toLowerCase() !== "individuals") return false;
    if (!parentTransfer) return false;
    if (parentTransfer?.lovePrice <= 0) return false;
    return t.status === TransferStatusEnum.TRANSFER_CREATED;
  };

  const showSidebar = (t?: Transfer): boolean => {
    if (!t) return false;
    if (departmentData?.name?.toLowerCase() !== "individuals") return false;
    if (t?.status !== TransferStatusEnum.TRANSFER_CREATED && t?.status !== TransferStatusEnum.CONFIRMED) return false;
    return Number(parentTransfer?.lovePrice) > 0;
  }

  const handleCardPay = async (input: PaymentPanelPaymentInput) => {
    if (!transferInfo) return;

    const result = await payWithCard({
      transferId: '/transfers/' + transferInfo.uuid,
      uuid: transferInfo.uuid,
      tpv: Tpv.PAYCOMET,
      paymentCardUuid: '/web/payment_cards/' + input.selectedCardUuid,
    });
    if (result.paymentLink) window.location.href = result.paymentLink;
  };

  const handleLuxonPay = async () => {
    if (!transferInfo) return;
    const result = await payWithCard({
      transferId: '/transfers/' + transferInfo.uuid,
      uuid: transferInfo.uuid,
      tpv: Tpv.LUXON,
    });
    if (result.paymentLink) window.location.href = result.paymentLink;
  };

  if (isLoading) return <PublicLayout/>;

  const hasPaymentPanel = showPaymentPanel(transferInfo);
  const hasSidebar = showSidebar(transferInfo);

  return (
    <PublicLayout>
      <div className={`nc-CheckOutPage`} data-nc-id="CheckOutPage">
        <main className="container mt-11 mb-12 lg:mb-18">
          <div
            className="w-full flex flex-col sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-6 px-0 sm:p-6 xl:p-8">
            <h2 className="text-3xl lg:text-4xl font-semibold">{t("transferOk.message.yourTransfer")}</h2>
            <AlertStatusSection transferInfo={transferInfo} hasSidebar={hasSidebar}/>
            <div className="border-b border-neutral-200 dark:border-neutral-700 mb-5"></div>
            <div className={`flex flex-col ${hasSidebar ? 'lg:flex-row lg:items-start lg:gap-10' : ''} px-0 sm:px-2`}>
              {transferInfo && (
                <div className={`flex flex-col space-y-6 mb-6 ${hasSidebar ? 'w-full lg:w-3/5 xl:w-2/3' : 'w-full'}`}>
                  <ConfirmationNumberCard transferInfo={transferInfo} parentTransfer={parentTransfer}/>
                  <PersonalDetailsSection transferInfo={transferInfo}/>
                  <EventDetailsSection eventInfo={eventInfo} cityInfo={cityInfo}/>
                  <TransportDetailsSection transferInfo={transferInfo}/>
                  <TransferDetailsSection transferInfo={transferInfo} locationFrom={locationFrom} locationTo={locationTo} parentTransfer={parentTransfer} route={route || undefined}/>
                  {/*<TotalToPaySection transferInfo={transferInfo} parentTransfer={parentTransfer} showPrice={showPaymentPanel(transferInfo)}/>*/}
                </div>
              )}
              {/* ── Right Panel: financial panel (mobile: below the content, desktop: sticky sidebar) ── */}
              {/* IMPORTANT: PaymentPanel is rendered ONLY ONCE to avoid duplicate IDs in the DOM */}
              {/* which would break the initialization of the Paycomet iframes (getElementById returns only the first one) */}
              {hasSidebar && transferInfo && parentTransfer && (
                <div className="flex flex-col w-full lg:w-2/5 xl:w-1/3 flex-shrink-0">
                  <div className="lg:sticky lg:top-6 flex flex-col space-y-4">
                    <TransferPriceSummary transferInfo={transferInfo} parentTransfer={parentTransfer} />
                    {hasPaymentPanel && (
                      <PaymentPanel
                        bookingId={transferInfo.id}
                        bookingInternalId={transferInfo.uuid}
                        amount={parentTransfer.lovePrice}
                        currencySymbol={parentTransfer.currency.symbol || '€'}
                        currency={transferInfo.currency || ''}
                        authenticated={authenticated}
                        preferredCardUuid={transferCardUuid}
                        tpvPaymentLastDate={null}
                        tpvPaymentInitialPayPercentage={null}
                        onCardPay={handleCardPay}
                        onLuxonPay={handleLuxonPay}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="text-center mt-10">
              <ButtonPrimary className="uppercase" href={`/transfer-requests/event-city/${cityEventInfo?.id}/department/${departmentData?.id}`}>
                {t("transferOk.message.bookAnotherTransfer")}
              </ButtonPrimary>
            </div>
          </div>
        </main>
      </div>
    </PublicLayout>
  );
}

export default PageTransferOK;
